import { useEffect, useState } from 'react'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { AlertTriangle, LogIn, LogOut } from 'lucide-react'
import ToggleSwitch from '../../app/components/atoms/ToggleSwitch'
import { ClampWithTooltip } from './ClampWithTooltip'
import { getAdastraLogoUrl } from '../../_metronic/helpers'
import type { CcpAuthState } from '../../types/amazon-connect-streams'

export type ConnectIdleAuthPhase =
    | 'initializing'
    | 'restoring'
    | 'logging_in'
    | 'login_required'
    | 'auth_error'
    | 'idle'

interface ConnectIdleShellProps {
    phase: ConnectIdleAuthPhase
    ccpAuthState: CcpAuthState
    isLoggedIn: boolean
    isInitialized: boolean
    agentName: string
    agentEmail: string
    agentStatus: string
    statusColor: string
    availabilityBlockReason: 'precall' | 'microphone' | 'softphone' | null
    tokenPromptActive: boolean
    onLogin: () => void
    onSignOut: () => void
    onStatusToggle: (checked: boolean) => void
    onAuthRecovery: () => void
}

const AUTH_ERROR_COPY = {
    auth_retries_exhausted: {
        title: 'Connect Authorization Failed',
        message: 'Amazon Connect could not verify your session after multiple attempts. Refresh this page or sign in again.',
        action: 'Refresh Page',
    },
    cti_auth_retries_exhausted: {
        title: 'Connect Session Expired',
        message: 'Call controls lost authorization. Refresh this page to restore your Connect session.',
        action: 'Refresh Page',
    },
    access_denied: {
        title: 'Access Denied',
        message: 'Your Connect account does not have permission to use this instance. Contact your administrator.',
        action: 'Try Again',
    },
    session_expired: {
        title: 'Session Expired',
        message: 'Your Connect session has ended. Sign in again to receive calls.',
        action: 'Sign In',
    },
} as const

export function getConnectIdleAuthPhase(params: {
    isLoggedIn: boolean
    isInitialized: boolean
    ccpAuthState: CcpAuthState
    isSessionProbePending: boolean
    isAuthorizing: boolean
}): ConnectIdleAuthPhase {
    if (params.isLoggedIn) return 'idle'
    if (params.isSessionProbePending) return 'restoring'
    if (params.ccpAuthState === 'ccp_loading' || !params.isInitialized) return 'initializing'
    if (params.isAuthorizing) return 'logging_in'
    if (
        params.ccpAuthState === 'auth_retries_exhausted' ||
        params.ccpAuthState === 'cti_auth_retries_exhausted' ||
        params.ccpAuthState === 'access_denied' ||
        params.ccpAuthState === 'session_expired'
    ) {
        return 'auth_error'
    }
    return 'login_required'
}

function StatusBarSkeleton() {
    return (
        <>
            <div className='d-flex flex-row align-items-center gap-3 flex-grow-1' style={{ minWidth: 0 }}>
                <div className='cw-skeleton cw-skeleton-avatar flex-shrink-0' aria-hidden='true' />
                <div className='d-flex flex-column gap-2 flex-grow-1' style={{ minWidth: 0 }}>
                    <div className='cw-skeleton cw-skeleton-line cw-skeleton-line--title' aria-hidden='true' />
                    <div className='cw-skeleton cw-skeleton-line cw-skeleton-line--subtitle' aria-hidden='true' />
                </div>
            </div>
            <div className='cw-skeleton cw-skeleton-toggle flex-shrink-0' aria-hidden='true' />
        </>
    )
}

function AuthWaitingIcon({ label }: { label: string }) {
    return (
        <div className='cw-auth-waiting-icon' role='img' aria-label={label}>
            <div className='cw-icon-circle bg-light-danger text-danger' style={{ width: 50, height: 50 }}>
                <LogIn size={22} />
            </div>
        </div>
    )
}

function LoggingInContent() {
    const [showSlowWarning, setShowSlowWarning] = useState(false)

    useEffect(() => {
        const timer = window.setTimeout(() => setShowSlowWarning(true), 15000)
        return () => window.clearTimeout(timer)
    }, [])

    return (
        <div className='text-center px-3'>
            <h4 className='fw-bold text-dark mb-2'>Signing In</h4>
            <p className='text-muted fs-7 mb-2'>
                Finish signing in through the popup window.
            </p>
            <p className='text-muted fs-8 mb-0 opacity-75'>
                This panel updates automatically when you&apos;re connected.
            </p>
            {showSlowWarning && (
                <div
                    className='cw-login-slow-warning mt-3'
                    role='alert'
                >
                    <AlertTriangle size={16} className='flex-shrink-0' aria-hidden='true' />
                    <span>
                        If your session doesn&apos;t connect after signing in through the popup, refresh this page and close the popup manually.
                    </span>
                </div>
            )}
        </div>
    )
}

function renderPhaseIcon(phase: ConnectIdleAuthPhase) {
    if (phase === 'restoring') {
        return <AuthWaitingIcon label='Restoring Connect session' />
    }

    if (phase === 'logging_in') {
        return <AuthWaitingIcon label='Waiting for Connect sign-in' />
    }

    if (phase === 'login_required' || phase === 'auth_error') {
        return (
            <div className='cw-icon-circle bg-light-danger text-danger' style={{ width: 50, height: 50 }}>
                <LogIn size={22} />
            </div>
        )
    }

    return null
}

function renderUpperContent(
    phase: ConnectIdleAuthPhase,
    ccpAuthState: CcpAuthState,
    onLogin: () => void,
    onAuthRecovery: () => void,
) {
    if (phase === 'idle') return null

    if (phase === 'initializing') {
        return (
            <div className='text-center px-3'>
                <h4 className='fw-bold text-dark mb-2'>Setting Up Connect Panel</h4>
                <p className='text-muted fs-7 mb-2'>
                    Please wait while we set up the Connect panel for you.
                </p>
                <p className='text-muted mb-0 fs-7 fw-semibold'>The portal must load before you can receive calls.</p>
            </div>
        )
    }

    if (phase === 'restoring') {
        return (
            <div className='text-center px-3'>
                <h4 className='fw-bold text-dark mb-2'>Restoring Connect Session</h4>
                <p className='text-muted fs-7 mb-0'>
                    Please wait while we restore your Connect session.
                </p>
            </div>
        )
    }

    if (phase === 'logging_in') {
        return <LoggingInContent />
    }

    if (phase === 'login_required') {
        return (
            <div className='text-center px-3'>
                <h4 className='fw-bold text-dark mb-2'>Sign In Required</h4>
                <p className='text-muted fs-7 mb-4'>Please log in to your Connect instance to begin.</p>
                <Button
                    variant='primary'
                    className='px-5 py-3 cw-action-btn fw-bold w-100'
                    style={{
                        backgroundColor: '#D7383C',
                        borderColor: '#D7383C',
                        boxShadow: '0 4px 14px rgba(215, 56, 60, 0.4)',
                    }}
                    onClick={onLogin}
                    aria-label='Log in to Connect'
                >
                    Log In
                </Button>
            </div>
        )
    }

    const copy = AUTH_ERROR_COPY[ccpAuthState as keyof typeof AUTH_ERROR_COPY]
    if (!copy) return null

    return (
        <div className='text-center px-3'>
            <h4 className='fw-bold text-dark mb-2'>{copy.title}</h4>
            <p className='text-muted fs-7 mb-4'>{copy.message}</p>
            <Button
                variant='primary'
                className='px-5 py-3 cw-action-btn fw-bold w-100'
                style={{
                    backgroundColor: '#D7383C',
                    borderColor: '#D7383C',
                    boxShadow: '0 4px 14px rgba(215, 56, 60, 0.4)',
                }}
                onClick={onAuthRecovery}
                aria-label={copy.action}
            >
                {copy.action}
            </Button>
        </div>
    )
}

function getLoaderLabel(phase: ConnectIdleAuthPhase): string {
    if (phase === 'restoring') return 'Restoring Connect session'
    if (phase === 'logging_in') return 'Waiting for Connect sign-in'
    return 'Initializing Connect panel'
}

export function ConnectIdleShell({
    phase,
    ccpAuthState,
    isLoggedIn,
    isInitialized,
    agentName,
    agentEmail,
    agentStatus,
    statusColor,
    availabilityBlockReason,
    tokenPromptActive,
    onLogin,
    onSignOut,
    onStatusToggle,
    onAuthRecovery,
}: ConnectIdleShellProps) {
    const showHorizontalLoader = phase === 'initializing' || phase === 'restoring' || phase === 'logging_in'
    const showLogo = phase === 'initializing' || phase === 'idle'

    return (
        <div className='cw-fade-in-up h-100 d-flex flex-column'>
            <div className='cw-idle-shell-upper flex-grow-1 d-flex flex-column'>
                <div className='cw-idle-shell-content'>
                    <div className='cw-idle-shell-visual'>
                        {showLogo ? (
                            <div className='cw-idle-shell-brand'>
                                <img src={getAdastraLogoUrl()} alt='Adastra' className='cw-idle-logo' />
                                <h2 className='text-center fw-bold mb-0' style={{ color: '#D1D5DB' }}>
                                    Connect Portal
                                </h2>
                            </div>
                        ) : (
                            renderPhaseIcon(phase)
                        )}
                    </div>
                    <div className='cw-idle-shell-message-row'>
                        {renderUpperContent(phase, ccpAuthState, onLogin, onAuthRecovery)}
                    </div>
                </div>
            </div>

            <div className='cw-idle-shell-footer bg-white'>
                {showHorizontalLoader && (
                    <div
                        className='cw-horizontal-loader'
                        role='progressbar'
                        aria-label={getLoaderLabel(phase)}
                    />
                )}
                <div className='cw-status-toggle-row px-4 py-3 d-flex justify-content-between align-items-center'>
                    {isLoggedIn ? (
                        <>
                            <div className='d-flex flex-row align-items-center gap-3' style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                <div className='cw-avatar-large text-white flex-shrink-0' style={{ background: '#374151' }}>
                                    {agentName.charAt(0).toUpperCase()}
                                </div>
                                <div className='d-flex flex-column align-items-start' style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                                    <div className='cw-selectable w-100' style={{ minWidth: 0 }}>
                                        <ClampWithTooltip text={agentName} className='fw-bold text-dark' style={{ fontSize: '1rem', lineHeight: 1.2 }} />
                                        <ClampWithTooltip text={agentEmail} className='small text-muted' />
                                    </div>
                                    <button
                                        type='button'
                                        className='btn cw-sign-out-btn text-danger border-0 rounded-pill mt-1 d-flex align-items-center gap-1 px-2 py-1'
                                        onClick={onSignOut}
                                        aria-label='Sign out of Connect'
                                        style={{ fontSize: '10px' }}
                                    >
                                        <LogOut size={9} aria-hidden='true' /> Sign out
                                    </button>
                                </div>
                            </div>
                            <div className='d-flex flex-column align-items-center gap-1'>
                                <OverlayTrigger
                                    placement='top'
                                    show={availabilityBlockReason ? undefined : false}
                                    overlay={
                                        <Tooltip id='agentStatusSwitchTooltip' className='toggle-switch-tooltip'>
                                            <div className='cw-availability-tooltip-head'>
                                                <i className='bi bi-exclamation-triangle-fill text-danger' aria-hidden='true' />
                                                <span className='fw-bold'>
                                                    {availabilityBlockReason === 'precall'
                                                        ? 'Pre-call questions pending'
                                                        : availabilityBlockReason === 'softphone'
                                                            ? 'Softphone recovery required'
                                                            : 'Microphone unavailable'}
                                                </span>
                                            </div>
                                        </Tooltip>
                                    }
                                >
                                    <span
                                        className='d-inline-flex'
                                        style={{
                                            cursor:
                                                !isInitialized || !isLoggedIn || availabilityBlockReason
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                        }}
                                    >
                                        <ToggleSwitch
                                            id='agentStatusSwitch'
                                            label=''
                                            ariaLabel={
                                                availabilityBlockReason === 'precall'
                                                    ? 'Agent status: Offline. Complete pending pre-call questions to go available'
                                                    : availabilityBlockReason === 'softphone'
                                                        ? 'Agent status: Offline. Complete softphone connection test to go available'
                                                        : availabilityBlockReason === 'microphone'
                                                            ? 'Agent status: Offline. Allow microphone access to go available'
                                                            : agentStatus === 'Available'
                                                                ? 'Set agent status to Offline'
                                                                : 'Set agent status to Available'
                                            }
                                            checked={agentStatus === 'Available' && !availabilityBlockReason}
                                            onChange={onStatusToggle}
                                            disabled={!isInitialized || !isLoggedIn || tokenPromptActive || !!availabilityBlockReason}
                                            helpTextClass='text-danger'
                                        />
                                    </span>
                                </OverlayTrigger>
                                <div className='fw-bold' style={{ color: statusColor, fontSize: '11px' }}>
                                    {availabilityBlockReason ? 'Offline' : agentStatus}
                                </div>
                            </div>
                        </>
                    ) : (
                        <StatusBarSkeleton />
                    )}
                </div>
            </div>
        </div>
    )
}
